import { spinner } from "@clack/prompts";
import { createHash } from "node:crypto";
import { existsSync, mkdtempSync, readFileSync, rmSync } from "node:fs";
import { tmpdir } from "node:os";
import { join, relative, resolve } from "node:path";
import chalk from "chalk";
import { Cli, Openapi, z } from "incur";
import { appsSpec, createWhopFetch, filterSpecByTag } from "../api/index.js";
import {
	createApp,
	createAppBuild,
	createDevAccessToken,
	getApp,
	listApps,
	promoteAppBuild,
	updateAppRoute,
	updateAppSecrets,
	uploadBuildArchive,
	waitForBuildPromotion,
	waitForFileReady,
	type WhopApp,
} from "./api.js";
import {
	APP_CONFIG_FILENAME,
	findProjectDir,
	readAppConfig,
	slugify,
	writeAppConfig,
	type AppConfig,
} from "./config.js";
import { commitSnapshot, gitInstalled, repoStatus } from "./git.js";
import { initOptions, initOutput, runInitFlow } from "./init.js";
import { pullOptions, pullOutput, runPullFlow } from "./pull.js";
import { extractSourceArchive, packSourceArchive } from "./source-archive.js";
import { findViteConfig, whopifyProject } from "./scaffold.js";
import {
	CommandAbort,
	cancelled,
	ensurePackageManager,
	hasScript,
	log,
	phase,
	promptForNewApp,
	promptSelect,
	promptText,
	runInstall,
	runScript,
	type CommandContext,
} from "./shared.js";

// The whop() vite plugin writes this archive into the build output directory.
const BUILD_ARCHIVE = "dist/whop-build.zip";

// Env names that control the local Node/package-manager runtime rather than
// the app. Injecting them from remotely-stored secrets would let anyone with
// developer:update_app on the app execute code on a teammate's machine the
// next time they run `whop apps dev`.
const UNSAFE_DEV_ENV_KEYS = new Set(["PATH"]);
// Whole families rather than named members: NODE_PATH was missing from the
// enumeration this replaces, and it redirects module resolution just as surely
// as NODE_OPTIONS injects code. Anything Node or a package manager reads to
// decide what to load is off limits.
const UNSAFE_DEV_ENV_PREFIXES = [
	"NODE_",
	"NPM_",
	"PNPM_",
	"YARN_",
	"BUN_",
	// corepack shims pnpm/yarn, and COREPACK_NPM_REGISTRY redirects where the
	// package manager itself is downloaded from.
	"COREPACK_",
	"LD_",
	"DYLD_",
];
// NODE_ENV picks the app's own mode; it loads nothing, and apps do ship it.
const SAFE_DEV_ENV_KEYS = new Set(["NODE_ENV"]);

/** Exported for tests — this filter is a security boundary, so it is asserted directly. */
export function isUnsafeDevEnvKey(key: string): boolean {
	const upper = key.toUpperCase();
	if (SAFE_DEV_ENV_KEYS.has(upper)) return false;
	return (
		UNSAFE_DEV_ENV_KEYS.has(upper) ||
		UNSAFE_DEV_ENV_PREFIXES.some((prefix) => upper.startsWith(prefix))
	);
}

const BuildSchema = z.object({
	id: z.string(),
	status: z.string(),
	is_production: z.boolean().optional(),
	url: z.string().optional(),
});

function formatManualSteps(steps: string[]): string {
	return steps.map((step, i) => `  ${i + 1}. ${step}`).join("\n");
}

function formatAppList(apps: WhopApp[]): string {
	const recent = [...apps]
		.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0))
		.slice(0, 10);
	return recent
		.map(
			(a) => `  ${a.id}  ${a.name}${a.route ? `  (${a.route}.whop.site)` : ""}`,
		)
		.join("\n");
}

/**
 * Registers the OpenAPI-generated commands for one tag of the apps spec
 * directly onto a command group. Done explicitly (instead of incur's
 * `{ fetch, openapi }` group registration) so generated commands and
 * hand-written ones like `deploy` can live in the same group.
 */
async function registerSpecCommands(
	group: ReturnType<typeof Cli.create>,
	tag: string,
): Promise<void> {
	const spec = filterSpecByTag(appsSpec as Record<string, unknown>, tag);
	const generated = await Openapi.generateCommands(
		spec as Openapi.OpenAPISpec,
		createWhopFetch(),
		{ config: { security: false } },
	);
	for (const [name, entry] of generated) {
		if ("run" in entry) group.command(name, entry);
	}
}

/**
 * Deploy ships an existing project — it does not scaffold. In a directory
 * that isn't a Vite app it composes instead: interactively it offers to run
 * `whop apps init`; in agent mode it names the commands that get the user a
 * project (init for a new app, pull for an existing one, --app to link code
 * that's already here).
 */
async function resolveDeployProject(
	c: CommandContext,
	dirOption: string | undefined,
): Promise<{ projectDir: string; freshInit: AppConfig | null }> {
	const projectDir = dirOption
		? resolve(dirOption)
		: (findProjectDir() ?? process.cwd());

	const isViteApp =
		existsSync(join(projectDir, "package.json")) &&
		findViteConfig(projectDir) !== undefined;
	if (isViteApp) return { projectDir, freshInit: null };

	if (c.agent) {
		throw new CommandAbort({
			code: "NOT_A_VITE_APP",
			message: [
				`${projectDir} doesn't look like a Vite app (missing package.json or vite config), and deploy only ships existing projects.`,
				"",
				"To start a new app here (scaffolds, registers, and links):",
				'  whop apps init --name "My Site" --route my-site --app_type b2c_app',
				"",
				"--app_type takes b2c_app or website. Ask the user which one: a website cannot be turned back into an app later.",
				"",
				"To get an existing app's deployed source onto this machine:",
				"  whop apps pull --app app_xxxxxxxx",
				"",
				"Then re-run `whop apps deploy` from inside the project directory.",
			].join("\n"),
			retryable: true,
			cta: {
				commands: [
					{ command: "apps init", description: "Scaffold a new Whop app" },
					{
						command: "apps pull",
						description: "Fetch an existing app's source",
					},
				],
			},
		});
	}

	const choice = await promptSelect(
		`${projectDir} doesn't look like a Vite app. What do you want to do?`,
		[
			{ value: "init", label: "Create a new Whop app (runs whop apps init)" },
			{ value: "cancel", label: "Cancel — I'll cd into my app directory" },
		],
	);
	if (choice === "cancel") cancelled();

	const scaffolded = await runInitFlow(c, {});
	return { projectDir: scaffolded.projectDir, freshInit: scaffolded.config };
}

/**
 * The project must be linked to a Whop app via whop.app.json. `--app` links
 * explicitly; otherwise offers to select or create one (always creating when
 * the account has no apps yet). In agent mode, prints the exact commands —
 * including recently created apps so the agent can ask the user about
 * reusing one.
 */
async function ensureAppLinked(
	c: CommandContext,
	projectDir: string,
	appOption: string | undefined,
): Promise<AppConfig> {
	if (appOption) {
		let app: WhopApp;
		try {
			app = await getApp(appOption);
		} catch (err) {
			throw new CommandAbort({
				code: "APP_NOT_FOUND",
				message: `Could not load app ${appOption}: ${err instanceof Error ? err.message : "unknown error"}`,
				retryable: true,
			});
		}
		const config = await withRoute(c, app);
		writeAppConfig(projectDir, config);
		log(
			c,
			chalk.dim(
				`Linked ${config.name} (${config.app_id}) → ${APP_CONFIG_FILENAME}`,
			),
		);
		return config;
	}

	const existing = readAppConfig(projectDir);
	if (existing) return existing;

	const apps = await listApps();

	if (c.agent) {
		const reuse =
			apps.length > 0
				? `This account has existing apps — ask the user whether to reuse one:\n${formatAppList(apps)}\n\nTo reuse one:  whop apps deploy --app <app_id>\n\n`
				: "This account has no apps yet, so a new one must be created.\n\n";
		throw new CommandAbort({
			code: "NO_APP_LINKED",
			message: [
				`No ${APP_CONFIG_FILENAME} here — this project isn't linked to a Whop app yet.`,
				"",
				`${reuse}To register a new app for this existing code, ask the user for a name and route (the route becomes <route>.whop.site), then run:`,
				'  whop apps create --name "My Site" --route my-site',
				"",
				"Then deploy with the returned app id:",
				"  whop apps deploy --app <app_id>",
			].join("\n"),
			retryable: true,
			cta: {
				commands: [
					{
						command: "apps create",
						description: "Register a new app (pass --name and --route)",
					},
					{
						command: "apps deploy",
						description: "Re-run with --app <app_id> to link and deploy",
					},
				],
			},
		});
	}

	let selected: WhopApp | null = null;
	if (apps.length > 0) {
		const recent = [...apps].sort(
			(a, b) => (b.created_at ?? 0) - (a.created_at ?? 0),
		);
		const choice = await promptSelect(
			"Which app should this project deploy to?",
			[
				...recent.map((a) => ({
					value: a.id,
					label: `${a.name} (${a.id}${a.route ? `, ${a.route}.whop.site` : ""})`,
				})),
				{ value: "__new__", label: "Register a new app" },
			],
		);
		if (choice !== "__new__") {
			selected = recent.find((a) => a.id === choice) ?? null;
		}
	}

	if (!selected) {
		const { name, route, appType } = await promptForNewApp();
		try {
			selected = await createApp({ name, route, app_type: appType });
		} catch (err) {
			throw new CommandAbort({
				code: "CREATE_FAILED",
				message:
					err instanceof Error ? err.message : "Failed to register the app",
				retryable: true,
			});
		}
	}

	const config = await withRoute(c, selected);
	writeAppConfig(projectDir, config);
	log(
		c,
		chalk.dim(
			`Linked ${config.name} (${config.app_id}) → ${APP_CONFIG_FILENAME}`,
		),
	);
	return config;
}

/**
 * Hosted deploys serve from <route>.whop.site, so a linked app must have a
 * route. Prompts for one interactively; in agent mode prints the exact
 * update command.
 */
async function withRoute(c: CommandContext, app: WhopApp): Promise<AppConfig> {
	if (app.route) return { app_id: app.id, name: app.name, route: app.route };

	if (c.agent) {
		throw new CommandAbort({
			code: "ROUTE_REQUIRED",
			message: `App ${app.id} (${app.name}) has no route — hosted apps are served from <route>.whop.site. Ask the user which route to claim, then run:\n  whop apps update ${app.id} --route <route>\n  whop apps deploy --app ${app.id}`,
			retryable: true,
			cta: {
				commands: [
					{
						command: `apps update ${app.id}`,
						description: "Set the route (pass --route)",
					},
				],
			},
		});
	}

	const slug = slugify(app.name);
	const routeInput = await promptText(
		`${app.name} has no route yet. Which route should it live at? (your-route.whop.site)`,
		slug,
		slug,
	);
	if (routeInput === null) cancelled();
	const route = slugify(routeInput);
	if (!route) {
		throw new CommandAbort({
			code: "ROUTE_REQUIRED",
			message: `Couldn't derive a route from "${routeInput}" — use letters and numbers (e.g. my-site).`,
			retryable: true,
		});
	}
	try {
		const updated = await updateAppRoute(app.id, route);
		return {
			app_id: updated.id,
			name: updated.name,
			route: updated.route ?? route,
		};
	} catch (err) {
		throw new CommandAbort({
			code: "UPDATE_FAILED",
			message: err instanceof Error ? err.message : "Failed to set the route",
			retryable: true,
		});
	}
}

/**
 * Deploy is the write side of pull's merge base: the uploaded source is what
 * the remote now serves, so the app's snapshot ref advances to match. The
 * machine that shipped last is then always "already up to date" on pull, and
 * other machines merge against the right ancestor. Best-effort — a deploy
 * never fails over bookkeeping.
 */
function advanceRemoteSnapshot(
	c: CommandContext,
	projectDir: string,
	appId: string,
	buildId: string,
	sourceZip: Uint8Array,
): void {
	if (!gitInstalled() || repoStatus(projectDir).kind !== "root") return;
	const snapshotDir = mkdtempSync(join(tmpdir(), "whop-deploy-"));
	try {
		extractSourceArchive(sourceZip, snapshotDir);
		commitSnapshot(
			projectDir,
			snapshotDir,
			appId,
			`whop apps deploy: ${appId} @ ${buildId}`,
			{ deriveFromHead: true },
		);
	} catch (err) {
		log(
			c,
			chalk.dim(
				`Couldn't record the deploy snapshot for \`whop apps pull\` (${err instanceof Error ? err.message : "unknown error"}).`,
			),
		);
	} finally {
		rmSync(snapshotDir, { recursive: true, force: true });
	}
}

function buildSecretsGroup() {
	const secrets = Cli.create("secrets", {
		description:
			"Manage app secrets — encrypted at rest, injected as env bindings into the hosted runtime and `whop apps dev`",
	});

	secrets.command("list", {
		description: "Show the app's secrets",
		options: z.object({
			app: z
				.string()
				.optional()
				.describe("App id (defaults to the project's linked app)"),
		}),
		run: async (c) => {
			const remote = await getApp(resolveLinkedAppId(c.options.app));
			return c.ok({ app_id: remote.id, secrets: remote.secrets ?? {} });
		},
	});

	secrets.command("set", {
		description: "Add or overwrite secrets",
		options: z.object({
			secret: z
				.array(z.string())
				.min(1)
				.describe("KEY=VALUE pair (repeatable)"),
			app: z
				.string()
				.optional()
				.describe("App id (defaults to the project's linked app)"),
		}),
		examples: [
			{ options: { secret: ["MAIL_API_KEY=mail-key-123"] } },
			{ options: { secret: ["A=1", "B=2"], app: "app_xxxxxxxx" } },
		],
		run: async (c) => {
			const updates: Record<string, string> = {};
			for (const pair of c.options.secret) {
				const eq = pair.indexOf("=");
				if (eq <= 0) {
					return c.error({
						code: "INVALID_SECRET",
						message: `"${pair}" is not KEY=VALUE. Use --secret NAME=value (an empty value deletes the secret).`,
					});
				}
				updates[pair.slice(0, eq)] = pair.slice(eq + 1);
			}
			const remote = await updateAppSecrets(
				resolveLinkedAppId(c.options.app),
				updates,
			);
			return c.ok({ app_id: remote.id, secrets: remote.secrets ?? {} });
		},
	});

	secrets.command("unset", {
		description: "Delete secrets by name",
		options: z.object({
			key: z.array(z.string()).min(1).describe("Secret name (repeatable)"),
			app: z
				.string()
				.optional()
				.describe("App id (defaults to the project's linked app)"),
		}),
		examples: [{ options: { key: ["MAIL_API_KEY"] } }],
		run: async (c) => {
			const updates: Record<string, string | null> = {};
			for (const key of c.options.key) updates[key] = null;
			const remote = await updateAppSecrets(
				resolveLinkedAppId(c.options.app),
				updates,
			);
			return c.ok({ app_id: remote.id, secrets: remote.secrets ?? {} });
		},
	});

	return secrets;
}

function resolveLinkedAppId(appOption: string | undefined): string {
	if (appOption) return appOption;
	const projectDir = findProjectDir();
	const config = projectDir ? readAppConfig(projectDir) : null;
	if (config) return config.app_id;
	throw new Error(
		`No app specified. Pass --app app_xxx or run inside a project with ${APP_CONFIG_FILENAME}.`,
	);
}

export async function buildAppGroup() {
	const app = Cli.create("apps", {
		description:
			"Build and deploy fully-hosted web apps on Whop (*.whop.site) — Vite apps served from Cloudflare Workers",
	});

	await registerSpecCommands(app, "Apps");

	const builds = Cli.create("builds", {
		description: "Manage app builds (list, inspect, promote)",
	});
	await registerSpecCommands(builds, "App Builds");
	app.command(builds);

	app.command(buildSecretsGroup());

	app.command("init", {
		description: "Start a new app: register, scaffold, and link a project",
		hint: "Registers the app on Whop (claiming <route>.whop.site), scaffolds the latest TanStack Start template — or a template app's published source with --template — wires it for Whop hosting, installs dependencies, and git-inits. Then `whop apps dev` to build, `whop apps deploy` to ship.",
		options: initOptions,
		output: initOutput,
		outputPolicy: "agent-only",
		examples: [
			{ description: "Scaffold a new app interactively" },
			{
				options: { name: "My Site", route: "my-site", app_type: "website" },
				description:
					"Scaffold without prompts (agents: always pass --name and --app_type)",
			},
			{
				options: { name: "My Site", template: "app_xxxxxxxx" },
				description: "Start from another app's published source archive",
			},
		],
		run: async (c) => {
			try {
				const result = await runInitFlow(c, c.options);
				const cdTarget = relative(process.cwd(), result.projectDir) || ".";

				if (!c.agent && !c.formatExplicit) {
					console.log(
						[
							"",
							chalk.green.bold(`✓ Created ${result.config.name}`),
							`  ${chalk.dim(result.config.app_id)}  ${result.config.route}.whop.site`,
							"",
							chalk.bold("Next"),
							...(cdTarget !== "." ? [`  cd ${cdTarget}`] : []),
							`  ${chalk.cyan.bold("whop apps dev")}      ${chalk.dim("→ local dev server")}`,
							`  ${chalk.cyan.bold("whop apps deploy")}   ${chalk.dim("→ ship it live")}`,
							"",
						].join("\n"),
					);
				}

				return c.ok(
					{
						app_id: result.config.app_id,
						name: result.config.name,
						route: result.config.route,
						dir: result.projectDir,
						manual_steps: result.manualSteps.length
							? result.manualSteps
							: undefined,
					},
					{
						cta: {
							commands: [
								{ command: "apps dev", description: "Run the dev server" },
								{ command: "apps deploy", description: "Ship it live" },
							],
						},
					},
				);
			} catch (err) {
				if (err instanceof CommandAbort) return c.error(err.options);
				return c.error({
					code: "INIT_FAILED",
					message: err instanceof Error ? err.message : "Init failed",
					retryable: true,
				});
			}
		},
	});

	app.command("pull", {
		description:
			"Fetch the app's deployed source and merge it into this directory",
		hint: "Downloads the current production build's source archive (or --build abld_xxx for any specific build — including rollback inspection) and three-way merges it with your local code via git: a per-app snapshot ref supplies the merge base, local edits are autostashed around the merge, and conflicts surface as normal git conflict markers. On a fresh machine it creates ./<route> and becomes your working copy.",
		options: pullOptions,
		output: pullOutput,
		outputPolicy: "agent-only",
		examples: [
			{ description: "Pull the linked app's deployed source" },
			{
				options: { app: "app_xxxxxxxx" },
				description: "Get an existing app's code onto a fresh machine",
			},
			{
				options: { build: "abld_xxxxxxxx" },
				description: "Materialize a specific build's source",
			},
			{
				options: { overwrite: true },
				description: "Skip the merge and write the files as-is",
			},
		],
		run: async (c) => {
			try {
				const result = await runPullFlow(c.options);

				if (!c.agent && !c.formatExplicit) {
					const summary = {
						merged: "✓ Pulled and merged the deployed source",
						up_to_date: "✓ Already up to date",
						overwritten: "✓ Wrote the deployed source over the working tree",
					}[result.status];
					console.log(
						[
							"",
							chalk.green.bold(summary),
							`  ${chalk.dim(`${result.app_id}${result.build_id ? ` @ ${result.build_id}` : ""}`)}  ${result.dir}`,
							...(result.autostash === "kept"
								? [
										chalk.yellow(
											"  Your uncommitted changes are still stashed (`git stash pop` conflicted) — pop them when ready.",
										),
									]
								: []),
							...(result.status === "merged"
								? [chalk.dim("  Review with `git log` / `git diff HEAD~1`.")]
								: []),
							"",
						].join("\n"),
					);
				}

				return c.ok(result, {
					cta: {
						commands: [
							{ command: "apps dev", description: "Run the dev server" },
						],
					},
				});
			} catch (err) {
				if (err instanceof CommandAbort) return c.error(err.options);
				return c.error({
					code: "PULL_FAILED",
					message: err instanceof Error ? err.message : "Pull failed",
					retryable: true,
				});
			}
		},
	});

	app.command("dev", {
		description: "Run the local dev server for this app",
		hint: "Starts the project's dev script with WHOP_APP_ID and WHOP_ACCOUNT_ID set and a short-lived access token injected as WHOP_API_KEY (minted from your CLI credential), so server-side SDK calls work locally without env setup. Explicitly exported WHOP_API_KEY and WHOP_ACCOUNT_ID values are used as-is. Pass --port/--host to bind somewhere specific, and --public_host when the server is reached through a proxy — the whop() vite plugin uses it for allowed hosts and the HMR socket.",
		options: z.object({
			dir: z
				.string()
				.optional()
				.describe("Project directory (defaults to the current directory)"),
			port: z.number().optional().describe("Port to bind the dev server to"),
			host: z
				.string()
				.optional()
				.describe(
					"Address to bind to. Use 0.0.0.0 when the server must be reachable from outside this machine",
				),
			public_host: z
				.string()
				.optional()
				.describe(
					"Origin browsers reach the dev server on (e.g. https://my-app.example.com) when it is served through a proxy",
				),
		}),
		run: async (c) => {
			const projectDir = c.options.dir
				? resolve(c.options.dir)
				: findProjectDir();
			const config = projectDir ? readAppConfig(projectDir) : null;
			if (!projectDir || !config) {
				return c.error({
					code: "NO_PROJECT",
					message: `No ${APP_CONFIG_FILENAME} found in this directory or any parent. Run \`whop apps init\` to start a new app, or \`whop apps pull --app app_xxx\` to fetch an existing one.`,
					cta: {
						commands: [
							{ command: "apps init", description: "Start a new app" },
							{
								command: "apps pull",
								description: "Fetch an existing app's source",
							},
						],
					},
				});
			}

			// Server-side SDK calls read WHOP_API_KEY; mint a short-lived access
			// token from the CLI's credential so they work in local dev with zero
			// env setup. An explicitly exported WHOP_API_KEY wins.
			const injected: Record<string, string> = {
				WHOP_APP_ID: config.app_id,
			};
			// The vite plugin reads these; they are passed as env rather than only
			// as script flags so they still apply when the project's dev script
			// pins its own --port.
			if (c.options.port !== undefined) {
				injected.WHOP_DEV_PORT = String(c.options.port);
			}
			if (c.options.host) injected.WHOP_DEV_HOST = c.options.host;
			if (c.options.public_host) {
				injected.WHOP_DEV_PUBLIC_ORIGIN = c.options.public_host;
			}
			if (process.env.WHOP_API_KEY) {
				log(
					c,
					chalk.dim(
						"Using the WHOP_API_KEY from your environment for server-side calls.",
					),
				);
			} else {
				try {
					const minted = await createDevAccessToken();
					injected.WHOP_API_KEY = minted.token;
					const expiresAt =
						typeof minted.expires_at === "number"
							? new Date(minted.expires_at * 1000)
							: minted.expires_at
								? new Date(minted.expires_at)
								: null;
					const expiry = expiresAt
						? ` (expires ${expiresAt.toLocaleTimeString()})`
						: "";
					log(
						c,
						chalk.dim(
							`Injected a temporary access token as WHOP_API_KEY${expiry} — server-side SDK calls just work. Restart \`whop apps dev\` to refresh it.`,
						),
					);
				} catch (err) {
					log(
						c,
						chalk.yellow(
							`Couldn't mint a temporary access token (${err instanceof Error ? err.message : "unknown error"}) — set WHOP_API_KEY manually for server-side calls.`,
						),
					);
				}
			}

			// Mirror the hosted runtime: the owning account and the app's secrets
			// become env vars for the dev server. Explicitly exported env vars win
			// over both.
			try {
				const remote = await getApp(config.app_id);
				if (remote.account?.id && !process.env.WHOP_ACCOUNT_ID) {
					injected.WHOP_ACCOUNT_ID = remote.account.id;
				}
				const secretEntries = Object.entries(remote.secrets ?? {}).filter(
					([key]) => !(key in process.env) && !(key in injected),
				);
				const unsafe = secretEntries.filter(([key]) => isUnsafeDevEnvKey(key));
				for (const [key, value] of secretEntries) {
					if (!isUnsafeDevEnvKey(key)) injected[key] = value;
				}
				if (unsafe.length > 0) {
					log(
						c,
						chalk.yellow(
							`Skipped app secret${unsafe.length === 1 ? "" : "s"} ${unsafe.map(([key]) => key).join(", ")} — these names control the local runtime and are never injected by \`whop apps dev\`.`,
						),
					);
				}
				const injectedCount = secretEntries.length - unsafe.length;
				if (injectedCount > 0) {
					log(
						c,
						chalk.dim(
							`Injected ${injectedCount} app secret${injectedCount === 1 ? "" : "s"} into the environment (${secretEntries
								.filter(([key]) => !isUnsafeDevEnvKey(key))
								.map(([key]) => key)
								.join(", ")}).`,
						),
					);
				}
			} catch (err) {
				log(
					c,
					chalk.yellow(
						`Couldn't fetch the app record (${err instanceof Error ? err.message : "unknown error"}) — continuing without its secrets or WHOP_ACCOUNT_ID.`,
					),
				);
			}

			log(c, chalk.dim(`Starting dev server for ${config.name}...`));
			// Also passed as flags: a dev script that hardcodes its own --port
			// would otherwise beat anything the plugin sets from config.
			const devArgs = [
				...(c.options.port !== undefined
					? ["--port", String(c.options.port)]
					: []),
				...(c.options.host ? ["--host", c.options.host] : []),
			];
			try {
				runScript(projectDir, "dev", injected, devArgs);
			} catch (err) {
				return c.error({
					code: "DEV_FAILED",
					message:
						err instanceof Error
							? err.message
							: "Dev server exited with an error",
				});
			}
			return c.ok({ success: true });
		},
	});

	app.command("deploy", {
		description: "Build, upload and ship this app live",
		hint: "Builds the linked project, typechecks, uploads the build + source archives, and promotes to production. Use --preview to upload without promoting, then promote later with `whop apps builds promote <build_id>` — promoting an older build is also how you roll back. To start a new project use `whop apps init`; to fetch an existing app's code use `whop apps pull`.",
		options: z.object({
			dir: z
				.string()
				.optional()
				.describe("Project directory (defaults to the current directory)"),
			app: z
				.string()
				.optional()
				.describe(
					"Link this project to an app (app_xxx) before deploying — writes whop.app.json, replacing any existing link",
				),
			preview: z
				.boolean()
				.optional()
				.describe("Upload the build without promoting it to production"),
			skip_typecheck: z
				.boolean()
				.optional()
				.describe("Skip the typecheck step"),
			skip_build: z
				.boolean()
				.optional()
				.describe("Skip the build step and upload the existing dist/ output"),
		}),
		output: BuildSchema,
		outputPolicy: "agent-only",
		examples: [
			{ description: "Build and ship to production" },
			{
				options: { app: "app_xxxxxxxx" },
				description: "Link this project to an existing app, then deploy",
			},
			{
				options: { preview: true },
				description: "Upload a preview build without promoting",
			},
		],
		run: async (c) => {
			try {
				ensurePackageManager();

				const { projectDir, freshInit } = await resolveDeployProject(
					c,
					c.options.dir,
				);
				const config =
					freshInit ?? (await ensureAppLinked(c, projectDir, c.options.app));

				const { manualSteps } = whopifyProject({
					targetDir: projectDir,
					route: config.route,
				});
				if (manualSteps.length > 0) {
					return c.error({
						code: "WHOPIFY_INCOMPLETE",
						message: `${config.name} is linked (${config.app_id}), but the project isn't fully wired for Whop hosting yet. Complete these steps:\n\n${formatManualSteps(manualSteps)}\n\nThen re-run \`whop apps deploy\` — it re-checks the wiring and applies anything it can.`,
					});
				}

				if (!c.options.skip_build) {
					if (!existsSync(join(projectDir, "node_modules", "@whop", "cli"))) {
						// whopifyProject adds @whop/cli as a devDependency (the vite
						// config imports whop() from @whop/cli/vite), so the build needs
						// it installed.
						phase("install");
						runInstall(c, projectDir);
					}
					// Build BEFORE typecheck: framework builds generate typed artifacts
					// the typecheck depends on (TanStack's routeTree.gen), so a fresh
					// never-built project would otherwise always fail its first deploy.
					phase("build");
					log(c, chalk.dim("[1/4] Building..."));
					runScript(projectDir, "build", { WHOP_APP_ID: config.app_id });
					if (!c.options.skip_typecheck && hasScript(projectDir, "typecheck")) {
						phase("typecheck");
						log(c, chalk.dim("[2/4] Typechecking..."));
						runScript(projectDir, "typecheck");
					}
				}

				const archivePath = join(projectDir, BUILD_ARCHIVE);
				if (!existsSync(archivePath)) {
					return c.error({
						code: "NO_BUILD_ARCHIVE",
						message: `No build archive at ${BUILD_ARCHIVE}. Add the whop() plugin from @whop/cli/vite to your vite config, then build. See https://whop.com/docs/apps/hosting.`,
					});
				}

				const zip = readFileSync(archivePath);
				const sizeMb = (zip.byteLength / 1024 / 1024).toFixed(1);
				const checksum = createHash("sha256").update(zip).digest("hex");

				const sourceArchive = packSourceArchive(projectDir);
				if ("skipped" in sourceArchive) {
					log(
						c,
						chalk.yellow(
							`Skipping source archive upload: ${sourceArchive.skipped}`,
						),
					);
				} else if (sourceArchive.note) {
					log(c, chalk.dim(`Source archive: ${sourceArchive.note}`));
				}

				const spin = c.agent ? null : spinner();
				phase("upload_build");
				spin?.start(`[3/4] Uploading build archive (${sizeMb} MB)`);
				let build;
				try {
					const fileId = await uploadBuildArchive(
						new Uint8Array(zip),
						`${config.route}-build.zip`,
					);
					let sourceFileId: string | undefined;
					if ("zip" in sourceArchive) {
						const sourceSizeMb = (
							sourceArchive.zip.byteLength /
							1024 /
							1024
						).toFixed(1);
						phase("upload_source");
						spin?.message(
							`[3/4] Uploading source archive (${sourceArchive.fileCount} files, ${sourceSizeMb} MB)`,
						);
						sourceFileId = await uploadBuildArchive(
							sourceArchive.zip,
							`${config.route}-source.zip`,
						);
					}
					phase("process_archive");
					spin?.message("[3/4] Processing archive");
					await waitForFileReady(fileId);
					if (sourceFileId) await waitForFileReady(sourceFileId);
					phase("create_build");
					spin?.message("[3/4] Creating build");
					build = await createAppBuild({
						app_id: config.app_id,
						checksum,
						file_id: fileId,
						source_file_id: sourceFileId,
					});
					spin?.stop(`[3/4] Build uploaded (${build.id})`);
				} catch (err) {
					spin?.error("[3/4] Upload failed");
					throw err;
				}

				if ("zip" in sourceArchive) {
					advanceRemoteSnapshot(
						c,
						projectDir,
						config.app_id,
						build.id,
						sourceArchive.zip,
					);
				}

				if (!c.options.preview) {
					const promoteSpin = c.agent ? null : spinner();
					phase("promote");
					promoteSpin?.start("[4/4] Promoting to production");
					try {
						await promoteAppBuild(build.id);
						build = await waitForBuildPromotion(build.id);
						promoteSpin?.stop(
							build.is_production
								? "[4/4] Promoted to production"
								: `[4/4] Promotion pending (status: ${build.status})`,
						);
					} catch (err) {
						promoteSpin?.error("[4/4] Promote failed");
						throw err;
					}
				} else {
					log(c, chalk.dim("[4/4] Skipping promotion (--preview)"));
				}

				const promoted = build.is_production === true;
				const productionUrl = promoted
					? ((await getApp(config.app_id)).hosted_url ?? undefined)
					: undefined;

				if (!c.agent && !c.formatExplicit) {
					const cdTarget = relative(process.cwd(), projectDir) || ".";
					console.log(
						[
							"",
							chalk.green.bold(
								promoted ? "✓ Deployed to production" : "✓ Build uploaded",
							),
							`  ${chalk.dim(build.id)}`,
							"",
							...(promoted && productionUrl
								? [
										`  ${chalk.bold("Live")}  ${chalk.cyan.underline(productionUrl)}`,
									]
								: [
										`${chalk.bold("Promote when ready")}  ${chalk.cyan.bold(`whop apps builds promote ${build.id}`)}`,
									]),
							...(freshInit
								? [
										"",
										chalk.bold("Next"),
										...(cdTarget !== "." ? [`  cd ${cdTarget}`] : []),
										`  ${chalk.cyan.bold("whop apps dev")}      ${chalk.dim("→ local dev server")}`,
										`  ${chalk.cyan.bold("whop apps deploy")}   ${chalk.dim("→ ship your changes")}`,
									]
								: []),
							"",
						].join("\n"),
					);
				}

				return c.ok(
					{
						id: build.id,
						status: build.status,
						is_production: promoted,
						url: productionUrl,
					},
					promoted
						? undefined
						: {
								cta: {
									commands: [
										{
											command: `apps builds promote ${build.id}`,
											description: "Promote this build to production",
										},
									],
								},
							},
				);
			} catch (err) {
				if (err instanceof CommandAbort) return c.error(err.options);
				return c.error({
					code: "DEPLOY_FAILED",
					message: err instanceof Error ? err.message : "Deploy failed",
					retryable: true,
				});
			}
		},
	});

	return app;
}
